import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import { getImageURL } from '../../utils/api';

export default function NotCorrectedOverdueList() {
  const navigate = useNavigate();

  const [staffCards, setStaffCards] = useState([]);
  const [totalOverdue, setTotalOverdue] = useState(0);
  const [totalPending, setTotalPending] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [thresholdDays, setThresholdDays] = useState(14);
  const [generatedAt, setGeneratedAt] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [onlyOverdue, setOnlyOverdue] = useState(false);

  const [coordinateCourses, setCoordinateCourses] = useState({});
  const [accessibleCourseIds, setAccessibleCourseIds] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [accessLoaded, setAccessLoaded] = useState(false);
  const isManagement = String(AuthService.getUser()?.superAdmin?.staff?.staffType || '').trim().toLowerCase() === 'management';

  // Load coordinate course access for the logged-in user
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const accessRes = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
        if (!isMounted) return;
        const data = accessRes.data || {};
        setCoordinateCourses(data.coordinateCourses || {});
        setAccessibleCourseIds(Array.isArray(data.accessibleCourseIds) ? data.accessibleCourseIds : []);
        setIsSuperAdmin(!!data.isSuperAdmin);
      } catch (e) {
        console.error('Failed to load coordinate courses', e);
      } finally {
        if (isMounted) setAccessLoaded(true);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const loadOverdue = useCallback(async () => {
    if (!accessLoaded) return;

    let accessibleParam = 'all';
    if (!isSuperAdmin && !isManagement) {
      const numericIds = (Array.isArray(accessibleCourseIds) ? accessibleCourseIds : [])
        .map((id) => Number(id))
        .filter((num) => Number.isFinite(num) && num > 0);

      if (numericIds.length === 0) {
        setStaffCards([]);
        setTotalOverdue(0);
        setTotalPending(0);
        setTotalItems(0);
        setLoading(false);
        return;
      }
      accessibleParam = numericIds;
    }

    setLoading(true);
    setError(null);
    try {
      const resp = await axiosInstance.get('/admin/assignment-summary/overdue', {
        params: { accessibleCourses: accessibleParam },
      });
      const data = resp?.data || {};
      setStaffCards(data.staffCards || []);
      setTotalOverdue(data.totalOverdue || 0);
      setTotalPending(data.totalPending || 0);
      setTotalItems(data.totalItems || 0);
      setThresholdDays(data.overdueThresholdDays || 14);
      setGeneratedAt(data.generatedAt || null);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load overdue assignments');
    } finally {
      setLoading(false);
    }
  }, [accessLoaded, accessibleCourseIds, isSuperAdmin, isManagement]);

  useEffect(() => {
    loadOverdue();
  }, [loadOverdue]);

  const filteredStaffCards = useMemo(() => {
    const term = search.trim().toLowerCase();

    return staffCards
      .map((staff) => {
        let items = staff.items || [];
        if (onlyOverdue) {
          items = items.filter((item) => item.isOverdue);
        }
        if (term) {
          items = items.filter((item) => {
            const combined = [
              item.subjectShortName,
              item.subjectName,
              item.batchLabel,
              item.courseShortName,
              item.courseName,
              staff.staffName,
            ]
              .filter(Boolean)
              .join(' ')
              .toLowerCase();
            return combined.includes(term);
          });
        }
        const overdueCount = items.filter((item) => item.isOverdue).length;
        return { ...staff, items, overdueCount, pendingCount: items.length - overdueCount, totalPending: items.length };
      })
      .filter((staff) => staff.items.length > 0)
      .sort((a, b) => (b.overdueCount - a.overdueCount) || (b.totalPending - a.totalPending));
  }, [staffCards, search, onlyOverdue]);

  const copyToClipboard = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      // best-effort only
    }
  };

  return (
    <DashboardLayout title="14 Days Passed Not Corrected Assignment">
      <div className="mx-auto max-w-7xl">
        <div className="mb-4">
          <button
            type="button"
            onClick={() => navigate('/admin/report/monitor')}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 border border-gray-300 rounded-lg text-gray-600 bg-white hover:bg-gray-50 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Monitor Report
          </button>
        </div>

        {/* Header banner */}
        <div className="mb-6 flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold text-red-800">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              14 Days Passed Not Corrected Assignment
            </h2>
            <p className="mt-1 text-sm text-red-900">
              Submitted assignments still not marked. Grouped by the lecturer allocated to each batch subject —
              flagged red once {thresholdDays}+ days have passed since submission.
            </p>
            {generatedAt && (
              <p className="mt-1 text-xs text-red-700/70">As of {new Date(generatedAt).toLocaleString()}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-3 text-sm">
            <div className="rounded-lg border border-red-300 bg-white px-4 py-2 text-center">
              <div className="text-lg font-bold text-red-700">{totalOverdue}</div>
              <div className="text-[11px] text-red-700/80">{thresholdDays}+ days</div>
            </div>
            <div className="rounded-lg border border-amber-300 bg-white px-4 py-2 text-center">
              <div className="text-lg font-bold text-amber-700">{totalPending}</div>
              <div className="text-[11px] text-amber-700/80">&lt; {thresholdDays} days</div>
            </div>
            <button
              onClick={loadOverdue}
              className="rounded-lg border border-red-600 bg-white px-4 py-2 font-medium text-red-700 transition hover:bg-red-100"
            >
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>
        )}

        {loading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-600">
            Loading overdue assignments…
          </div>
        ) : (
          <>
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex flex-col">
                  <label className="mb-1 text-xs font-medium text-gray-600">Search</label>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Staff / Subject / Batch / Course"
                    className="w-64 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-red-500 focus:outline-none"
                  />
                </div>
                <label className="mb-0.5 flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={onlyOverdue}
                    onChange={(e) => setOnlyOverdue(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                  />
                  Show only {thresholdDays}+ days overdue
                </label>
              </div>
              <div className="text-sm text-gray-600">Staff: {filteredStaffCards.length}</div>
            </div>

            {filteredStaffCards.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center">
                <svg className="mx-auto h-12 w-12 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900">Nothing to show</h3>
                <p className="mt-2 text-sm text-gray-600">
                  {totalItems === 0
                    ? 'No pending assignment corrections found.'
                    : 'No results match the current search/filter.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filteredStaffCards.map((staff) => {
                  return (
                    <div
                      key={staff.staffId}
                      className={`overflow-hidden rounded-xl border bg-white shadow-sm ${
                        staff.overdueCount > 0 ? 'border-red-200' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-3 p-4">
                        <img
                          src={getImageURL(staff.imageUrl)}
                          alt={staff.staffName}
                          className="h-12 w-12 rounded-full object-cover ring-1 ring-gray-200"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium text-gray-900">{staff.staffName}</div>
                          <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-600">
                            <span className="truncate">{staff.officeEmail || staff.gmail || '-'}</span>
                            {staff.officeEmail && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyToClipboard(staff.officeEmail);
                                }}
                                className="rounded border border-gray-300 px-1 py-0.5 hover:border-red-500 hover:text-red-700"
                              >
                                Copy
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 border-t border-gray-100 px-4 py-3">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${
                          staff.overdueCount > 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {staff.overdueCount} overdue ({thresholdDays}+ days)
                        </span>
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold ${
                          staff.pendingCount > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {staff.pendingCount} pending (&lt; {thresholdDays}d)
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() => window.open(`/admin/report/not-corrected-overdue/${staff.staffId}`, '_blank', 'noopener,noreferrer')}
                        className="flex w-full items-center justify-center gap-1.5 border-t border-gray-100 px-4 py-2.5 text-xs font-medium text-red-700 hover:bg-red-50"
                      >
                        View {staff.items.length} assignment{staff.items.length === 1 ? '' : 's'}
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
